import { camelCase } from 'lodash-es';
import { useEffect, useRef } from 'react';

import { CustomEventEmitter } from '@/util';

import type { Emitter, EventHandler, EventHandlerMap, EventName } from './type';

const useCustomEventEmitter = <E extends EventHandlerMap>(
  eventHandlerMap: E,
) => {
  const emitterRef = useRef(new CustomEventEmitter());
  const eventEmitterRef = useRef({
    on(name, handler) {
      emitterRef.current.on(name, handler);
    },
    off(name, handler) {
      emitterRef.current.off(name, handler);
    },
    emit(name, target, option) {
      emitterRef.current.emit(name, target, option);
    },
  } as Emitter<E>);

  useEffect(() => {
    const eventEntry = Object.entries(eventHandlerMap).map(([n, h]) => [
      camelCase(n.replace(/^on/i, '')),
      h,
    ]) as [
      CamelCase<SnakeCase<RemoveOnPrefix<EventName<E>>>>,
      EventHandler<E, EventName<E>>,
    ][];

    eventEntry.forEach(([name, handler]) => {
      eventEmitterRef.current.on(name, handler);
    });

    return () => {
      eventEntry.forEach(([name, handler]) => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        eventEmitterRef.current.off(name, handler);
      });
    };
  }, [eventHandlerMap]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      emitterRef.current.disposeEvent();
    };
  }, []);

  return { eventEmitter: eventEmitterRef };
};

export default useCustomEventEmitter;
